import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { login } from "../api/auth";
import { useAuth } from "../stores/auth";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = safeRedirect((location.state as { from?: string } | null)?.from);
  const setSession = useAuth((s) => s.setSession);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const data = await login(email, password);

      if ("challenge_token" in data) {
        navigate("/mfa/challenge", { state: { challengeToken: data.challenge_token, from } });
        return;
      }

      setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        mfaRequired: data.mfa_required,
      });
      navigate(from, { replace: true });
    } catch (e: unknown) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Welcome back</h1>
        <p className="mt-2 text-slate-600">Sign in to your office.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3"
              required
              autoComplete="email"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="block text-sm font-medium text-slate-700" htmlFor="password">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3"
              required
              autoComplete="current-password"
            />
          </div>

          {error ? (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-sm text-slate-600">
          New to KraetdNet?{" "}
          <Link to="/signup" className="font-medium text-slate-900 underline">
            Set up an office
          </Link>
        </p>
      </div>
    </main>
  );
}

function safeRedirect(path: string | undefined): string {
  if (path && path.startsWith("/") && !path.startsWith("//")) return path;
  return "/dashboard";
}

function extractError(e: unknown): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return "Wrong email or password.";
}
